import React, { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';
import projectsData from '../data/projects.json';
import { useNavigate } from 'react-router-dom';
import './Visualization.css';
import logoImage from '../img/physical-synthetic-logo.png';

function Visualization() {
  const svgRef = useRef();
  const navigate = useNavigate();
  const [imageMap, setImageMap] = useState({});

  // Preload all project images
  useEffect(() => {
    const loadImages = async () => {
      const imagePromises = projectsData.map(project => {
        return new Promise((resolve) => {
          if (project.previewImage) {
            const img = new Image();
            img.onload = () => resolve({ id: project.id, url: `${process.env.PUBLIC_URL}/${project.previewImage}` });
            img.onerror = () => resolve({ id: project.id, url: `${process.env.PUBLIC_URL}/img/physical-synthetic-logo.png` });
            img.src = `${process.env.PUBLIC_URL}/${project.previewImage}`;
          } else {
            resolve({ id: project.id, url: `${process.env.PUBLIC_URL}/img/physical-synthetic-logo.png` });
          }
        });
      });

      const loadedImages = await Promise.all(imagePromises);
      const imageMapData = {};
      loadedImages.forEach(({ id, url }) => {
        imageMapData[id] = url;
      });
      setImageMap(imageMapData);
    };

    loadImages();
  }, []);
  useEffect(() => {
    if (Object.keys(imageMap).length === 0) return; // Wait for images to load
    
    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove(); // Clear previous render
    
    // Use window dimensions for visualization sizing
    const width = window.innerWidth;
    const height = window.innerHeight;
    
    // Set size explicitly to ensure proper scaling
    svg.attr("width", width)
       .attr("height", height)
       .attr("viewBox", `0 0 ${width} ${height}`)
       .attr("preserveAspectRatio", "xMidYMid meet");

    // Group projects by cohort
    const projectsByCohort = d3.group(projectsData, d => d.cohort);
    const cohorts = Array.from(projectsByCohort.keys());
      // Territory markers with full descriptions
    const territoryMarkers = [
      { 
        id: 'body', 
        label: 'BODY', 
        description: "The body in synthetic realities is both a vehicle and a canvas, allowing users to experience and shape their digital presence. In these environments, the body takes the form of digital avatars— customizable digital stand-ins that can range from photorealistic human forms to fantastical creatures or abstract shapes. The experience is not just visual representation, but also extends to movement, sensation, and expression within the digital world. Technologies like motion capture, biofeedback, and neural interfaces enable translation of movements into the synthetic realm, creating a sense of embodiment where users feel physically connected to their digital counterparts. This embodiment can influence the experience of synthetic realities in visceral and emotional ways. It also raises questions about identity, privacy, and the psychological effects of inhabiting a body that can be endlessly manipulated and detached from one's physical self. As synthetic realities become more sophisticated, the concept of the body evolves, challenging traditional notions of what it means to be human in a world where the digital and the physical are increasingly intertwined.",
        x: width * 0.15, 
        y: height * 0.5 
      },
      { 
        id: 'context', 
        label: 'CONTEXT', 
        description: "Synthetic realities refer to digitally created environments or experiences that mimic, alter, or completely reinvent the real world. These realities are crafted through advanced technologies such as virtual reality (VR), augmented reality (AR), artificial intelligence (AI), and deep learning. The concept encompasses a broad range of applications, from immersive gaming and simulations to virtual social spaces and synthetic media. As technology evolves, the lines between real and synthetic experiences blur, raising questions about authenticity, identity, and the nature of human interaction. Synthetic realities are not just entertainment; they are becoming integral to fields like education, healthcare, and remote work, offering new possibilities while also presenting ethical and philosophical challenges.",
        x: width * 0.85, 
        y: height * 0.25 
      },
      { 
        id: 'environment', 
        label: 'ENVIRONMENT', 
        description: "The experience of synthetic realities is a dynamic and meticulously constructed digital space where users can interact with virtual objects, avatars, and landscapes. The environments are designed to be immersive, often replicating real-world settings with striking accuracy or creating entirely fantastical worlds that defy the laws of physics. In practice, the environment is shaped by a blend of computer-generated imagery (CGI), 3D models, and real-time rendering, all enhanced by AI-driven systems that adapt and respond to user actions. In these spaces, sensory experiences are crafted to engage sight, sound, and sometimes even touch, creating a compelling sense of presence. As users navigate these environments, they can manipulate and interact with elements in ways that would be impossible in the physical world, offering unprecedented freedom and creative potential. However, the artificial nature of these environments also prompts discussions about the psychological impacts of extended immersion, the potential for deception, and the broader societal implications of living in a world where digital and physical realities increasingly overlap.",
        x: width * 0.7, 
        y: height * 0.85 
      },
    ];    // Calculate cluster positions for each cohort - reduced radius to keep projects inside the screen
    const clusterPositions = cohorts.map((cohort, index) => {
      const angle = (index / cohorts.length) * 2 * Math.PI;
      const centerX = width * 0.5 + Math.cos(angle) * width * 0.35; // Reduced from 0.42 to 0.35
      const centerY = height * 0.5 + Math.sin(angle) * height * 0.35; // Reduced from 0.42 to 0.35
      return { cohort, x: centerX, y: centerY };
    });    // Create tooltip container with larger dimensions for territory descriptions
    const tooltipSize = 600; // Increased width for territory tooltips
    const tooltipHeight = 280; // Adjusted height for better aspect ratio// Track active tooltip for closing
    let activeTooltip = null;
    
    // Function to close any active tooltips
    const closeActiveTooltip = () => {
      if (activeTooltip) {
        activeTooltip.transition()
          .duration(200)
          .style('opacity', 0)
          .remove();
        activeTooltip = null;
      }
    };
      // Add click event on the SVG background to close any active tooltip
    svg.on('click', () => {
      // Close any active tooltip
      closeActiveTooltip();
      
      // Also close any hover tooltips
      svg.selectAll('.territory-hover-tooltip')
        .transition()
        .duration(150)
        .style('opacity', 0)
        .remove();
      
      // Reset all markers
      svg.selectAll('.territory-marker')
        .transition()
        .duration(200)
        .attr('r', 18)
        .attr('fill', 'rgba(255, 255, 255, 0.9)');
    });
      // Draw territory markers with higher z-index and click behavior
    svg.selectAll('.territory-marker-group')
      .data(territoryMarkers)
      .enter()
      .append('g') // Use a group for each marker to increase hit area
      .attr('class', d => `territory-marker-group territory-${d.id}`)
      .style('cursor', 'pointer')
      .style('pointer-events', 'all')
      // Add invisible larger circle for bigger hit area
      .each(function(d) {
        d3.select(this)
          .append('circle')
          .attr('class', 'territory-marker-hitarea')
          .attr('cx', d.x)
          .attr('cy', d.y)
          .attr('r', 25) // Larger invisible hit area
          .attr('fill', 'rgba(255, 255, 255, 0)')
          .style('pointer-events', 'all');
          
        d3.select(this)
          .append('circle')
          .attr('class', 'territory-marker')
          .attr('cx', d.x)
          .attr('cy', d.y)
          .attr('r', 18) 
          .attr('fill', 'rgba(255, 255, 255, 0.9)')
          .attr('stroke', 'rgba(255, 255, 255, 0.5)')
          .attr('stroke-width', 2);
      })      .on('mouseover', (event, d) => {
        // Remove any existing hover tooltips first to prevent overlapping
        svg.selectAll('.territory-hover-tooltip').remove();
        
        // Highlight marker
        d3.select(event.currentTarget).select('.territory-marker')
          .transition()
          .duration(200)
          .attr('r', 21)
          .attr('fill', 'rgba(255, 255, 255, 1)');// Create tooltip for hover effect only if no tooltip is active
        if (activeTooltip) return;        const tooltip = svg.append('g')
          .attr('class', 'territory-tooltip territory-hover-tooltip')
          .style('opacity', 0)
          .style('pointer-events', 'none'); // Make hover tooltip completely non-interactive
          
        const padding = 20;
        let tooltipX = d.x - tooltipSize/2;
        let tooltipY = d.y - tooltipHeight - 30;
        
        // Keep tooltip within bounds
        tooltipX = Math.max(padding, Math.min(tooltipX, width - tooltipSize - padding));
        tooltipY = Math.max(padding, Math.min(tooltipY, height - tooltipHeight - padding));
        
        // Use a consistent multi-color gradient for all tooltips
        const gradientColors = ['#ff8c00', '#4682b4', '#8a2be2']; // Deep Orange -> Steel Blue -> Blue Violet
        
        // Create gradient definition for hover tooltip
        const hoverGradientId = `hover-tooltip-gradient-${d.id}`;
        const gradient = tooltip.append('defs')
          .append('linearGradient')
          .attr('id', hoverGradientId)
          .attr('x1', '0%')
          .attr('y1', '0%')
          .attr('x2', '100%')
          .attr('y2', '0%');
          
        gradient.selectAll('stop')
          .data(gradientColors)
          .enter().append('stop')
          .attr('offset', (c, i) => `${(i / (gradientColors.length - 1)) * 100}%`)
          .attr('stop-color', c => c)
          .attr('stop-opacity', 0.75);
        
        // Tooltip background with gradient and no border radius
        tooltip.append('rect')
          .attr('x', tooltipX)
          .attr('y', tooltipY)
          .attr('width', tooltipSize)
          .attr('height', tooltipHeight)
          .attr('rx', 0) // No border radius - rectangular shape
          .attr('fill', `url(#${hoverGradientId})`) // Use the gradient
          .attr('stroke', 'none');        // Tooltip title
        tooltip.append('text')
          .attr('x', tooltipX + tooltipSize/2)
          .attr('y', tooltipY + 45) // Moved down slightly
          .attr('text-anchor', 'middle')
          .attr('fill', 'white')
          .attr('font-size', '28px') // Larger size for all-caps
          .attr('font-weight', '900')
          .text(d.label);
            // Tooltip description - scrollable container
        tooltip.append('foreignObject')
          .attr('x', tooltipX + 20)
          .attr('y', tooltipY + 55)
          .attr('width', tooltipSize - 40)
          .attr('height', tooltipHeight - 80)
          .style('pointer-events', 'all') // Enable pointer events for scrolling
          .append('xhtml:div')
          .attr('class', 'scrollable-description')
          .attr('xmlns', 'http://www.w3.org/1999/xhtml')
          .style('color', 'white')
          .style('font-size', '14px') // Smaller font for all-caps
          .style('font-weight', 'bold')
          .style('line-height', '1.6') // Increased line height for better readability
          .style('padding', '5px')
          .style('text-align', 'justify')
          .style('overflow-y', 'auto') // Enable vertical scrolling
          .style('max-height', (tooltipHeight - 80) + 'px')          .style('scrollbar-width', 'thin')
          .style('text-transform', 'uppercase') // Add all-caps text
          .style('letter-spacing', '0.5px') // Add letter spacing for better readability
          .style('scrollbar-color', 'rgba(255, 255, 255, 0.6) transparent') // Thin white scrollbar, transparent background
          .style('pointer-events', 'auto') // Allow scrolling within the content
          .html(() => {
            // Use a single paragraph for the description to avoid layout issues
            return `<p style="pointer-events: none;">${d.description}</p><div class="scroll-indicator" style="pointer-events: none;">⬍</div>`;
          });
          
        // Small scroll indicator at bottom of tooltip
        tooltip.append('text')
          .attr('x', tooltipX + tooltipSize - 30)
          .attr('y', tooltipY + tooltipHeight - 15)
          .attr('text-anchor', 'middle')
          .attr('fill', 'rgba(255, 255, 255, 0.5)')
          .attr('font-size', '12px')
          .text('scroll');        // Animate tooltip
        tooltip.transition()
          .duration(200)
          .style('opacity', 1);
      })
      .on('mouseout', (event) => {
        // Delay hiding the tooltip to give users time to move the mouse to it
        let tooltipHideDelay;
        
        // Reset marker only if no tooltip is active
        if (!activeTooltip) {
          d3.select(event.currentTarget).select('.territory-marker')
            .transition()
            .duration(200)
            .attr('r', 18)
            .attr('fill', 'rgba(255, 255, 255, 0.9)');
        }
        
        // Store reference to any tooltip
        const hoverTooltip = svg.selectAll('.territory-hover-tooltip');
        
        // Cancel any existing timeout
        clearTimeout(tooltipHideDelay);
          // Add a shorter delay before hiding the tooltip
        tooltipHideDelay = setTimeout(() => {
          // Check if the mouse is over the tooltip before removing it
          const tooltipNode = hoverTooltip.node();
          
          if (tooltipNode) {
            const mouseOverTooltip = document.querySelectorAll('.territory-tooltip:hover').length > 0;
            
            // Only remove if mouse isn't over the tooltip
            if (!mouseOverTooltip) {
              hoverTooltip
                .transition()
                .duration(150)
                .style('opacity', 0)
                .remove();
            }
          }
        }, 150); // Reduced delay to 150ms for faster responsiveness
      })
      // Add click handler for territory markers
      .on('click', function(event, d) {
        // Close any active tooltip first
        closeActiveTooltip();
        
        // Create new tooltip with interaction enabled
        const tooltip = svg.append('g')
          .attr('class', 'territory-tooltip territory-click-tooltip')
          .style('opacity', 0)
          .style('pointer-events', 'all');
          
        // Set as active tooltip
        activeTooltip = tooltip;
        
        const padding = 20;
        let tooltipX = d.x - tooltipSize/2;
        let tooltipY = d.y - tooltipHeight - 30;
        
        // Keep tooltip within bounds
        tooltipX = Math.max(padding, Math.min(tooltipX, width - tooltipSize - padding));
        tooltipY = Math.max(padding, Math.min(tooltipY, height - tooltipHeight - padding));
          // Use a consistent multi-color gradient for all tooltips
        const gradientColors = ['#ff8c00', '#4682b4', '#8a2be2']; // Deep Orange -> Steel Blue -> Blue Violet
        
        // Create gradient definition
        const gradientId = `tooltip-gradient-${d.id}`;
        const gradient = tooltip.append('defs')
          .append('linearGradient')
          .attr('id', gradientId)
          .attr('x1', '0%')
          .attr('y1', '0%')
          .attr('x2', '100%')
          .attr('y2', '0%');
          
        gradient.selectAll('stop')
          .data(gradientColors)
          .enter().append('stop')
          .attr('offset', (c, i) => `${(i / (gradientColors.length - 1)) * 100}%`)
          .attr('stop-color', c => c)
          .attr('stop-opacity', 0.95);
          
        // Tooltip background - rectangular with gradient
        tooltip.append('rect')
          .attr('x', tooltipX)
          .attr('y', tooltipY)
          .attr('width', tooltipSize)
          .attr('height', tooltipHeight)
          .attr('rx', 0) // No border radius - sharp corners
          .attr('fill', `url(#${gradientId})`)
          .attr('stroke', 'none'); // No stroke
          
        // Close button
        const closeButton = tooltip.append('g')
          .attr('class', 'tooltip-close-btn')
          .style('cursor', 'pointer');
          
        closeButton.append('circle')
          .attr('cx', tooltipX + tooltipSize - 20)
          .attr('cy', tooltipY + 20)
          .attr('r', 12)
          .attr('fill', 'rgba(255, 255, 255, 0.2)');
          
        closeButton.append('text')
          .attr('x', tooltipX + tooltipSize - 20)
          .attr('y', tooltipY + 25)
          .attr('text-anchor', 'middle')
          .attr('fill', 'white')
          .attr('font-size', '16px')
          .attr('font-weight', 'bold')
          .text('×');
          
        closeButton.on('click', function(event) {
          event.stopPropagation();
          closeActiveTooltip();
          
          // Reset marker
          d3.select(event.currentTarget.parentNode.parentNode)
            .select('.territory-marker')
            .transition()
            .duration(200)
            .attr('r', 18)
            .attr('fill', 'rgba(255, 255, 255, 0.9)');
        });
          // Tooltip title
        tooltip.append('text')
          .attr('x', tooltipX + tooltipSize/2)
          .attr('y', tooltipY + 45)
          .attr('text-anchor', 'middle')
          .attr('fill', 'white')
          .attr('font-size', '28px') // Larger font for all-caps
          .attr('font-weight', '900')
          .text(d.label);
          
        // Tooltip description - scrollable container
        tooltip.append('foreignObject')
          .attr('x', tooltipX + 20)
          .attr('y', tooltipY + 55)
          .attr('width', tooltipSize - 40)
          .attr('height', tooltipHeight - 80)
          .style('pointer-events', 'all') // Enable pointer events on foreignObject for scrolling
          .append('xhtml:div')
          .attr('class', 'scrollable-description')
          .attr('xmlns', 'http://www.w3.org/1999/xhtml')
          .style('color', 'white')
          .style('font-size', '14px') // Smaller font for all-caps
          .style('font-weight', 'bold')
          .style('line-height', '1.6') // Increased line height for better readability
          .style('padding', '5px')
          .style('text-align', 'justify')
          .style('overflow-y', 'auto')
          .style('max-height', (tooltipHeight - 80) + 'px')
          .style('scrollbar-width', 'thin')          .style('pointer-events', 'auto') // Important for scrolling
          .style('text-transform', 'uppercase') // Add all-caps text
          .style('letter-spacing', '0.5px') // Add letter spacing for better readability          .style('scrollbar-color', 'rgba(255, 255, 255, 0.6) transparent') // Thin white scrollbar, transparent background
          .html(() => {
            // Use a single paragraph for the description to avoid layout issues
            return `<p>${d.description}</p><div class="scroll-indicator">⬍</div>`;
          });
          
        // Small scroll indicator at bottom of tooltip
        tooltip.append('text')
          .attr('x', tooltipX + tooltipSize - 30)
          .attr('y', tooltipY + tooltipHeight - 15)
          .attr('text-anchor', 'middle')
          .attr('fill', 'rgba(255, 255, 255, 0.5)')
          .attr('font-size', '12px')
          .text('scroll');
          
        // Animate tooltip in
        tooltip.transition()
          .duration(200)
          .style('opacity', 1);
          
        // Prevent event bubbling
        event.stopPropagation();
      });    // Add territory marker labels
    svg.selectAll('.territory-label')
      .data(territoryMarkers)
      .enter()
      .append('text')
      .attr('class', 'territory-label')
      .attr('x', d => d.x)
      .attr('y', d => d.y - 20)
      .attr('text-anchor', 'middle')
      .attr('fill', 'rgba(255, 255, 255, 0.8)')
      .attr('font-size', '12px')
      .attr('font-weight', 'bold')
      .attr('letter-spacing', '1px') // Add letter spacing for better readability
      .style('pointer-events', 'none') // Ensure labels don't interfere with marker interactions
      .text(d => d.label);

    // Create project clusters and simulations
    const allProjects = [];
    const simulations = [];

    clusterPositions.forEach(({ cohort, x: clusterX, y: clusterY }) => {
      const cohortProjects = projectsByCohort.get(cohort) || [];
      
      // Ensure projects have required properties
      cohortProjects.forEach(project => {
        project.correlations = project.correlations || { body: 5, environment: 5, context: 5 };
        project.size = project.size || 'small';
        project.clusterX = clusterX;
        project.clusterY = clusterY;
        project.cohort = cohort;
        
        // Calculate position within cluster based on correlations
        const bodyWeight = project.correlations.body / 10;
        const envWeight = project.correlations.environment / 10;
        const contextWeight = project.correlations.context / 10;
        
        // Position relative to territory markers
        const bodyInfluence = { x: territoryMarkers[0].x, y: territoryMarkers[0].y };
        const contextInfluence = { x: territoryMarkers[1].x, y: territoryMarkers[1].y };
        const envInfluence = { x: territoryMarkers[2].x, y: territoryMarkers[2].y };
          // Calculate target position based on correlations
        const targetX = clusterX + 
          (bodyInfluence.x - clusterX) * bodyWeight * 0.15 +
          (contextInfluence.x - clusterX) * contextWeight * 0.15 +
          (envInfluence.x - clusterX) * envWeight * 0.15;
          
        const targetY = clusterY + 
          (bodyInfluence.y - clusterY) * bodyWeight * 0.15 +
          (contextInfluence.y - clusterY) * contextWeight * 0.15 +
          (envInfluence.y - clusterY) * envWeight * 0.15;
        
        project.targetX = targetX;
        project.targetY = targetY;
        
        // Initialize position
        project.x = clusterX + (Math.random() - 0.5) * 50;
        project.y = clusterY + (Math.random() - 0.5) * 50;      });      
      
      // Create simulation for this cohort
      const simulation = d3.forceSimulation(cohortProjects)
        .force('x', d3.forceX(d => d.size === 'large' ? d.clusterX : d.targetX).strength(0.7)) // Increased strength from 0.5 to 0.7
        .force('y', d3.forceY(d => d.size === 'large' ? d.clusterY : d.targetY).strength(0.7)) // Increased strength from 0.5 to 0.7
        .force('collision', d3.forceCollide(d => d.size === 'large' ? 120 : 85).strength(1.0)) // Adjusted collision radius and max strength
        .force('charge', d3.forceManyBody().strength(-50)) // Reduced repulsion
        .force('bounds', () => {
          // Keep projects strictly within screen bounds with stronger enforcement
          cohortProjects.forEach(d => {
            const tileSize = d.size === 'large' ? 100 : 75; // Increased half-size to ensure more padding
            d.x = Math.max(tileSize, Math.min(width - tileSize, d.x));
            d.y = Math.max(tileSize, Math.min(height - tileSize, d.y));
          });
        })
        .alphaTarget(0.1)
        .velocityDecay(0.8);

      simulations.push(simulation);
      allProjects.push(...cohortProjects);
    });    // Draw project tiles with lower z-index than territory markers
    const projectTiles = svg.selectAll('.project-tile')
      .data(allProjects, d => d.id)
      .enter()
      .append('g')
      .attr('class', 'project-tile')
      .style('cursor', 'pointer')
      .style('pointer-events', 'all'); // Ensure pointer events are enabled// Add project tile backgrounds (white borders)
    projectTiles.append('rect')
      .attr('class', 'tile-border')
      .attr('width', d => d.size === 'large' ? 180 : 130) // Increased large size from 160 to 180
      .attr('height', d => d.size === 'large' ? 180 : 130) // Increased large size from 160 to 180
      .attr('rx', 4)
      .attr('fill', 'white')
      .attr('stroke', 'rgba(255, 255, 255, 0.8)')
      .attr('stroke-width', 2);    // Add project images
    projectTiles.append('image')
      .attr('class', 'tile-image')
      .attr('width', d => d.size === 'large' ? 172 : 122) // Increased large size from 152 to 172
      .attr('height', d => d.size === 'large' ? 172 : 122) // Increased large size from 152 to 172
      .attr('x', 4)
      .attr('y', 4)
      .attr('href', d => imageMap[d.id] || `${process.env.PUBLIC_URL}/img/physical-synthetic-logo.png`)
      .attr('preserveAspectRatio', 'xMidYMid slice')
      .style('border-radius', '2px');

    // Add project tooltips and click handlers
    projectTiles
      .on('mouseover', (event, d) => {        // Create project tooltip
        const tooltip = svg.append('g')
          .attr('class', 'project-tooltip')
          .style('opacity', 0);        // Calculate better title length based on actual string width (approximately 7px per character for 14px size)
        const maxTitleChars = 35; // Reduced from 50 to avoid overflow
        const titleText = d.title && d.title.length > maxTitleChars ? 
          d.title.substring(0, maxTitleChars) + '...' : 
          d.title || d.id;
        
        // Fixed tooltip sizing
        const tooltipWidth = 280; // Slightly wider to accommodate text better
        const tooltipHeight = 60; // Standard height for title and cohort only
        let tooltipX = d.x + (d.size === 'large' ? 80 : 55); // Adjusted for new sizes
        let tooltipY = d.y - tooltipHeight - 10;
        
        // Keep tooltip within bounds
        tooltipX = Math.max(10, Math.min(tooltipX, width - tooltipWidth - 10));
        tooltipY = Math.max(10, Math.min(tooltipY, height - tooltipHeight - 10));        // Tooltip background - increased transparency
        tooltip.append('rect')
          .attr('x', tooltipX)
          .attr('y', tooltipY)
          .attr('width', tooltipWidth)
          .attr('height', tooltipHeight)
          .attr('rx', 6)
          .attr('fill', 'rgba(0, 0, 0, 0.7)') // Increased transparency from 0.9 to 0.7
          .attr('stroke', 'rgba(255, 255, 255, 0.3)')
          .attr('stroke-width', 1);// Tooltip text - title with improved positioning
        tooltip.append('text')
          .attr('x', tooltipX + tooltipWidth/2)
          .attr('y', tooltipY + 25)
          .attr('text-anchor', 'middle')
          .attr('fill', 'white')
          .attr('font-size', '14px')
          .attr('font-weight', 'bold')
          .text(titleText)
          .attr('class', 'tooltip-title-text');

        // Cohort info
        tooltip.append('text')
          .attr('x', tooltipX + tooltipWidth/2)
          .attr('y', tooltipY + 45)
          .attr('text-anchor', 'middle')
          .attr('fill', 'rgba(255, 255, 255, 0.7)')
          .attr('font-size', '11px')
          .attr('class', 'tooltip-cohort-text')
          .text(d.cohort.replace('_', ' ').toUpperCase());

        // Animate tooltip
        tooltip.transition()
          .duration(200)
          .style('opacity', 1);

        // Highlight tile
        d3.select(event.currentTarget)
          .select('.tile-border')
          .transition()
          .duration(200)
          .attr('stroke-width', 4)
          .attr('stroke', 'rgba(255, 255, 255, 1)');
      })
      .on('mouseout', (event) => {
        // Remove tooltip
        svg.selectAll('.project-tooltip')
          .transition()
          .duration(200)
          .style('opacity', 0)
          .remove();

        // Reset tile highlight
        d3.select(event.currentTarget)
          .select('.tile-border')
          .transition()
          .duration(200)
          .attr('stroke-width', 2)
          .attr('stroke', 'rgba(255, 255, 255, 0.8)');
      })
      .on('click', (event, d) => {
        navigate(`/projects/${d.cohort}/${d.id}`);
      });

    // Bring territory markers to the front so they are always clickable,
    // making sure they appear above the project tiles
    svg.selectAll('.territory-marker-group').raise();
    svg.selectAll('.territory-label').raise();

    // Animation loop for gentle movement
    let animationId;
      const animate = () => {
      const time = Date.now() * 0.0005;      // Update territory markers with gentle movement (increased amplitude for visibility)
      territoryMarkers.forEach((marker, i) => {
        const offset = i * Math.PI * 2 / territoryMarkers.length;
        marker.currentX = marker.x + Math.sin(time + offset) * 10; // Increased from 6 to 10
        marker.currentY = marker.y + Math.cos(time + offset) * 10; // Increased from 6 to 10
      });
        // Update territory marker positions (both visible marker and hit area)
      svg.selectAll('.territory-marker')
        .attr('cx', d => d.currentX || d.x)
        .attr('cy', d => d.currentY || d.y);
        
      svg.selectAll('.territory-marker-hitarea')
        .attr('cx', d => d.currentX || d.x)
        .attr('cy', d => d.currentY || d.y);
      
      svg.selectAll('.territory-label')
        .attr('x', d => d.currentX || d.x)
        .attr('y', d => (d.currentY || d.y) - 20);      // Add gentle continuous movement to project tiles (increased amplitude)
      allProjects.forEach((project, i) => {
        const offset = i * 0.1; // Different offset for each project
        const gentleX = Math.sin(time * 0.8 + offset) * 6; // Increased from 4 to 6
        const gentleY = Math.cos(time * 0.6 + offset) * 5; // Increased from 3 to 5
        
        // Apply gentle movement on top of simulation position
        project.animatedX = project.x + gentleX;
        project.animatedY = project.y + gentleY;
      });

      // Update project tile positions with gentle animation
      projectTiles
        .attr('transform', d => `translate(${(d.animatedX || d.x) - (d.size === 'large' ? 90 : 65)}, ${(d.animatedY || d.y) - (d.size === 'large' ? 90 : 65)})`);

      // Update project positions from simulations with additional boundary enforcement
      simulations.forEach(simulation => {
        simulation.on('tick', () => {
          // Ensure all projects remain within view bounds on each tick
          simulation.nodes().forEach(d => {
            const tileSize = d.size === 'large' ? 100 : 75;
            d.x = Math.max(tileSize, Math.min(width - tileSize, d.x));
            d.y = Math.max(tileSize, Math.min(height - tileSize, d.y));
          });
        });
      });

      animationId = requestAnimationFrame(animate);
    };

    animate();

    // Cleanup function
    return () => {
      if (animationId) {
        cancelAnimationFrame(animationId);
      }
      simulations.forEach(simulation => simulation.stop());
    };

  }, [navigate, imageMap]);

  return (
    <div className="visualization-wrapper">
      <img src={logoImage} alt="Logo" className="logo-image" />
      <svg ref={svgRef} className="visualization-container"></svg>
    </div>
  );
}

export default Visualization;
